export type RouterAnswerMap = Record<string, string>;
export const CUSTOM_ROUTER_ANSWER_ID = "__other__";
export const MAX_CUSTOM_ROUTER_ANSWER_LENGTH = 280;
const customRouterAnswerPrefix = `${CUSTOM_ROUTER_ANSWER_ID}:`;

export interface IndustryClassificationBoundary {
  against_industry_id: string;
  rule: string;
}

export interface IndustryMetadata {
  id: string;
  menu_label: string;
  title: string;
  definition: string;
  include_signals: string[];
  exclude_signals: string[];
  examples: string[];
  classification_boundaries: IndustryClassificationBoundary[];
  questionnaire_id: string | null;
  status: "active" | "coming_soon";
}

export interface RouterOption {
  answer_id: string;
  label: string;
  score_values: Record<string, number>;
}

export interface RouterQuestion {
  question_id: string;
  section_id: string;
  prompt: string;
  options: RouterOption[];
}

export interface QuestionnaireSection {
  id: string;
  label: string;
}

export interface Questionnaire {
  questionnaire_id: string;
  industry_id: string;
  title: string;
  candidate_lens_topic_ids: string[];
  default_lens_topic_id: string;
  tie_break_question_ids: string[];
  sections: QuestionnaireSection[];
  questions: RouterQuestion[];
}

export interface RouterMetadata {
  router_version: string;
  industries: IndustryMetadata[];
  questionnaires: Record<string, Questionnaire>;
}

export interface LensTopicAttribute {
  thinking_method_id: string;
  attribute_value: string;
}

export interface LensTopicMethod {
  id: string;
  name: string;
  author: string;
  status: string;
}

export function getIndustry(metadata: RouterMetadata, industryId: string): IndustryMetadata | null {
  return metadata.industries.find((industry) => industry.id === industryId) ?? null;
}

export function isAnonymousWizardResumeExpired(
  resumeExpiresAt: string,
  userId: string | null | undefined,
  now = Date.now(),
): boolean {
  return !userId && new Date(resumeExpiresAt).getTime() <= now;
}

export function getQuestionOption(question: RouterQuestion, answerId: string | undefined): RouterOption | null {
  if (!answerId) return null;
  const customAnswer = getCustomRouterAnswerText(answerId);
  if (customAnswer) {
    return {
      answer_id: CUSTOM_ROUTER_ANSWER_ID,
      label: customAnswer,
      score_values: {},
    };
  }
  return question.options.find((option) => option.answer_id === answerId) ?? null;
}

export function createCustomRouterAnswer(value: string): string {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (!normalized) throw new Error("Custom answer is required");
  if (normalized.length > MAX_CUSTOM_ROUTER_ANSWER_LENGTH) {
    throw new Error(`Custom answer must be ${MAX_CUSTOM_ROUTER_ANSWER_LENGTH} characters or fewer`);
  }
  return `${customRouterAnswerPrefix}${normalized}`;
}

export function getCustomRouterAnswerText(value: string | undefined): string | null {
  if (!value?.startsWith(customRouterAnswerPrefix)) return null;
  const customAnswer = value.slice(customRouterAnswerPrefix.length).trim();
  return customAnswer || null;
}

export function resolvePublishedLensTopics(
  topics: string[],
  topicAttributes: LensTopicAttribute[],
  methods: LensTopicMethod[],
) {
  const methodById = new Map(methods.map((method) => [method.id, method]));
  return [...new Set(topics)].map((topic) => {
    const matches = topicAttributes
      .filter((row) => row.attribute_value === topic)
      .map((row) => methodById.get(row.thinking_method_id))
      .filter((method): method is LensTopicMethod => method?.status === "published");
    if (matches.length === 0) throw new Error(`Lens topic '${topic}' has no published thinking method`);
    if (matches.length > 1) throw new Error(`Lens topic '${topic}' resolves to multiple published thinking methods`);
    return { id: matches[0].id, topic, name: matches[0].name, author: matches[0].author };
  });
}
